import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { useSidebarState } from '../hooks/useSidebarState';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, Upload, FileText, Download, Plus, Trash2,
  AlertTriangle, CheckCircle, XCircle, ArrowRight,
  Globe, DollarSign, Calendar, TrendingUp, BarChart3,
  ChevronDown, ChevronUp, Search, Filter, Eye, Zap
} from 'lucide-react';
import { generateIEEPAPdf } from '../utils/pdfGenerator';
import { apiGet, apiPost, apiPut, apiDelete, apiFetch } from '../lib/apiClient';

const IEEPARecovery = ({ user, setUser }) => {
  const sidebarCollapsed = useSidebarState();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [scans, setScans] = useState([]);
  const [claims, setClaims] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [autoScanning, setAutoScanning] = useState(false);
  const [manualEntries, setManualEntries] = useState([createBlankEntry()]);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimLoading, setClaimLoading] = useState(false);
  const [newNote, setNewNote] = useState('');

  function createBlankEntry() {
    return { entry_number: '', entry_date: '', country_of_origin: '', hs_code: '', description: '', customs_value: '', duty_paid: '' };
  }

  const fetchDashboard = useCallback(async () => {
    try {
      const data = await apiGet('/api/ieepa/dashboard');
      setDashboard(data);
    } catch { /* ignore */ }
  }, []);

  const fetchScans = useCallback(async () => {
    try {
      const d = await apiGet('/api/ieepa/scans');
      setScans(d.scans || []);
    } catch { /* ignore */ }
  }, []);

  const fetchClaims = useCallback(async () => {
    try {
      const d = await apiGet('/api/ieepa/claims');
      setClaims(d.claims || []);
    } catch { /* ignore */ }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const d = await apiGet('/api/ieepa/reference/countries', { auth: false });
      setCountries(d.countries || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchDashboard(), fetchScans(), fetchClaims(), fetchCountries()])
      .finally(() => setLoading(false));
  }, []);

  const openClaimDetail = async (claimId) => {
    setClaimLoading(true);
    try {
      const data = await apiGet(`/api/ieepa/claims/${claimId}`);
      setSelectedClaim(data);
      setActiveTab('claim-detail');
    } catch { toast.error('Failed to load claim'); }
    finally { setClaimLoading(false); }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedClaim) return;
    try {
      const note = await apiPost(`/api/ieepa/claims/${selectedClaim.claim_id}/notes`, { text: newNote.trim() });
      setSelectedClaim(prev => ({ ...prev, notes: [...(prev.notes || []), note] }));
      setNewNote('');
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  const deleteNote = async (noteId) => {
    if (!selectedClaim) return;
    try {
      await apiDelete(`/api/ieepa/claims/${selectedClaim.claim_id}/notes/${noteId}`);
      setSelectedClaim(prev => ({ ...prev, notes: (prev.notes || []).filter(n => n.note_id !== noteId) }));
      toast.success('Note deleted');
    } catch { toast.error('Failed to delete note'); }
  };

  const toggleChecklist = async (itemId, checked) => {
    if (!selectedClaim) return;
    try {
      await apiPut(`/api/ieepa/claims/${selectedClaim.claim_id}/checklist`, { item_id: itemId, checked });
      setSelectedClaim(prev => ({
        ...prev,
        checklist: (prev.checklist || []).map(c => c.id === itemId ? { ...c, checked } : c)
      }));
    } catch { toast.error('Failed to update checklist'); }
  };

  const updateClaimStatus = async (newStatus) => {
    if (!selectedClaim) return;
    try {
      const data = await apiPut(`/api/ieepa/claims/${selectedClaim.claim_id}/status?status=${newStatus}`);
      const now = new Date().toISOString();
      setSelectedClaim(prev => ({
        ...prev,
        status: newStatus,
        timeline: [...(prev.timeline || []), { event: 'status_change', old_status: data.old_status, status: newStatus, user_name: user?.name || 'You', timestamp: now }]
      }));
      fetchClaims();
      toast.success(`Status updated to ${newStatus}`);
    } catch { toast.error('Failed to update status'); }
  };

  const handleManualScan = async () => {
    const valid = manualEntries.filter(e => e.entry_date && e.country_of_origin && e.customs_value);
    if (!valid.length) { toast.error('Add at least one entry with date, country, and value'); return; }
    setScanning(true);
    try {
      const scan = await apiPost('/api/ieepa/scan/manual', {
        entries: valid.map(e => ({ ...e, customs_value: parseFloat(e.customs_value) || 0, duty_paid: e.duty_paid ? parseFloat(e.duty_paid) : null }))
      });
      toast.success(`Scan complete: ${scan.total_eligible_entries} eligible entries found`);
      setSelectedScan(scan);
      setActiveTab('results');
      fetchDashboard(); fetchScans();
      setManualEntries([createBlankEntry()]);
    } catch (e) { toast.error(e?.message || 'Scan failed'); }
    finally { setScanning(false); }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const scan = await apiPost('/api/ieepa/scan/csv', formData);
      toast.success(`CSV scan complete: ${scan.total_eligible_entries} eligible entries`);
      setSelectedScan(scan);
      setActiveTab('results');
      fetchDashboard(); fetchScans();
    } catch (err) { toast.error(err?.message || 'Upload failed'); }
    finally { setScanning(false); e.target.value = ''; }
  };

  const handleAutoScan = async () => {
    setAutoScanning(true);
    try {
      const scan = await apiPost('/api/ieepa/scan/auto');
      if (scan.scan_id) {
        toast.success(`Auto-scan complete: ${scan.total_eligible_entries} eligible entries, $${scan.total_recovery?.toLocaleString()} recovery`);
        setSelectedScan(scan);
        setActiveTab('results');
        fetchDashboard(); fetchScans();
      } else {
        toast.info(scan.message || 'No import data found to scan');
      }
    } catch (e) { toast.error(e?.message || 'Auto-scan failed'); }
    finally { setAutoScanning(false); }
  };

  const handleExportPDF = () => {
    if (!selectedScan) return;
    try {
      const pdf = generateIEEPAPdf(selectedScan);
      pdf.save(`ieepa_recovery_${selectedScan.scan_id}.pdf`);
      toast.success('PDF exported');
    } catch (e) { toast.error('PDF export failed'); console.error(e); }
  };

  const handleExportCAPE = async (scanId) => {
    try {
      const res = await apiFetch(`/api/ieepa/scans/${scanId}/cape-export`, { raw: true });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `cape_submission_${scanId}.csv`; a.click();
        URL.revokeObjectURL(url);
        toast.success('CAPE CSV exported');
      } else { toast.error('Export failed'); }
    } catch { toast.error('Export error'); }
  };

  const handleViewScan = async (scanId) => {
    try {
      const data = await apiGet(`/api/ieepa/scans/${scanId}`);
      setSelectedScan(data);
      setActiveTab('results');
    } catch { toast.error('Failed to load scan'); }
  };

  const handleCreateClaim = async (scanId) => {
    try {
      await apiPost(`/api/ieepa/claims?scan_id=${scanId}`);
      toast.success('Claim draft created');
      fetchClaims();
    } catch (e) { toast.error(e?.message || 'Failed'); }
  };

  const addManualEntry = () => setManualEntries(prev => [...prev, createBlankEntry()]);
  const removeManualEntry = (idx) => setManualEntries(prev => prev.filter((_, i) => i !== idx));
  const updateEntry = (idx, field, val) => setManualEntries(prev => prev.map((e, i) => i === idx ? { ...e, [field]: val } : e));

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'scan', label: 'New Scan', icon: Search },
    { id: 'results', label: 'Results', icon: Eye },
    { id: 'history', label: 'History', icon: FileText },
    { id: 'claims', label: 'Claims', icon: DollarSign },
    { id: 'countries', label: 'Reference', icon: Globe },
  ];

  if (loading) return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      <Sidebar user={user} setUser={setUser} activePage="ieepa" />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></main>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      <Sidebar user={user} setUser={setUser} activePage="ieepa" />
      <main className="flex-1 p-6 lg:p-8 lg:pt-24 transition-all duration-300" data-testid="ieepa-page">
        <div className="max-w-7xl">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0B3D91]" data-testid="ieepa-title">IEEPA Recovery Center</h1>
                <p className="text-sm text-slate-500">Identify and recover tariffs paid under IEEPA executive orders ruled unconstitutional</p>
              </div>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-800" data-testid="ieepa-disclaimer">
            <strong>Disclaimer:</strong> This tool provides estimates for informational purposes only. It does not constitute legal advice.
            Actual refund amounts depend on CBP processing and CAPE portal requirements. Consult a licensed customs broker for claims.
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 shadow-sm border border-slate-100 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  (activeTab === tab.id || (activeTab === 'claim-detail' && tab.id === 'claims')) ? 'bg-[#0B3D91] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`ieepa-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && dashboard && (
            <div className="space-y-6" data-testid="ieepa-dashboard">
              {/* KPIs */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Potential Refund', value: `$${dashboard.total_potential_refund?.toLocaleString()}`, icon: DollarSign, color: 'from-emerald-500 to-green-600' },
                  { label: 'Interest Accrued', value: `$${dashboard.total_interest_accrued?.toLocaleString()}`, icon: TrendingUp, color: 'from-blue-500 to-indigo-600' },
                  { label: 'Total Recovery', value: `$${dashboard.total_recovery?.toLocaleString()}`, icon: BarChart3, color: 'from-violet-500 to-purple-600' },
                  { label: 'Eligible Entries', value: dashboard.total_eligible_entries?.toLocaleString(), icon: FileText, color: 'from-orange-500 to-red-500' },
                ].map((kpi, i) => (
                  <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid={`ieepa-kpi-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-500">{kpi.label}</span>
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${kpi.color} flex items-center justify-center`}>
                        <kpi.icon className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold font-outfit text-slate-900">{kpi.value}</p>
                  </div>
                ))}
              </div>

              {/* IEEPA Period & Info */}
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <h3 className="font-outfit font-semibold text-[#0B3D91] mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5" /> IEEPA Period
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Start Date</span>
                      <span className="font-medium">{dashboard.ieepa_period?.start}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">End Date</span>
                      <span className="font-medium">{dashboard.ieepa_period?.end}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Countries Affected</span>
                      <span className="font-medium">{dashboard.reference?.countries_affected}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Interest Rate</span>
                      <span className="font-medium">{dashboard.reference?.interest_rate} annual</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <h3 className="font-outfit font-semibold text-[#0B3D91] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5" /> CAPE Portal Status
                  </h3>
                  <div className="space-y-3">
                    {dashboard.cape_status && Object.entries(dashboard.cape_status).map(([key, val]) => (
                      <div key={key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">{val.label}</span>
                          <span className="font-medium text-slate-900">{val.completion}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all" style={{ width: `${val.completion}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-3">Est. launch: {dashboard.reference?.cape_estimated_launch}</p>
                </div>
              </div>

              {/* Recent Scans */}
              {dashboard.recent_scans?.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <h3 className="font-outfit font-semibold text-[#0B3D91] mb-4">Recent Scans</h3>
                  <div className="space-y-2">
                    {dashboard.recent_scans.map(scan => (
                      <div key={scan.scan_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => handleViewScan(scan.scan_id)}>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{scan.scan_name}</p>
                          <p className="text-xs text-slate-500">{scan.total_entries} entries · {scan.total_eligible_entries} eligible</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-emerald-600">${scan.total_recovery?.toLocaleString()}</p>
                          <p className="text-xs text-slate-400">{new Date(scan.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick start */}
              {dashboard.total_scans === 0 && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center border border-blue-100">
                  <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2">Get Started</h3>
                  <p className="text-slate-600 mb-4 max-w-lg mx-auto">
                    Upload your import data or automatically scan your existing analyses to identify tariffs eligible for refund under the IEEPA ruling.
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <Button onClick={handleAutoScan} disabled={autoScanning} className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6" data-testid="ieepa-auto-scan-btn">
                      {autoScanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</> : <><Zap className="w-4 h-4 mr-2" /> Auto-Scan My Imports</>}
                    </Button>
                    <Button onClick={() => setActiveTab('scan')} variant="outline" className="rounded-full px-6" data-testid="ieepa-start-scan-btn">
                      Manual Entry <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Auto-scan CTA for existing users */}
              {dashboard.total_scans > 0 && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex items-center justify-between">
                  <div>
                    <h3 className="font-outfit font-semibold text-[#0B3D91]">Re-scan your import data</h3>
                    <p className="text-sm text-slate-500">Automatically check all your financial analyses for IEEPA-eligible entries</p>
                  </div>
                  <Button onClick={handleAutoScan} disabled={autoScanning} className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-5" data-testid="ieepa-rescan-btn">
                    {autoScanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</> : <><Zap className="w-4 h-4 mr-2" /> Auto-Scan</>}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Scan Tab */}
          {activeTab === 'scan' && (
            <div className="space-y-6" data-testid="ieepa-scan-tab">
              {/* Auto-Scan */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-outfit font-semibold text-emerald-900 flex items-center gap-2 mb-1">
                      <Zap className="w-5 h-5 text-emerald-600" /> Auto-Scan Existing Data
                    </h3>
                    <p className="text-sm text-emerald-700">Instantly scan all your financial analyses and ERP imports for IEEPA-eligible entries. No upload needed.</p>
                  </div>
                  <Button onClick={handleAutoScan} disabled={autoScanning} className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6" data-testid="ieepa-autoscan-tab-btn">
                    {autoScanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</> : <><Zap className="w-4 h-4 mr-2" /> Auto-Scan</>}
                  </Button>
                </div>
              </div>

              {/* CSV Upload */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="font-outfit font-semibold text-[#0B3D91] mb-3 flex items-center gap-2">
                  <Upload className="w-5 h-5" /> CSV Upload
                </h3>
                <p className="text-sm text-slate-500 mb-4">Upload a CSV with columns: entry_date, country_of_origin (ISO code), hs_code, customs_value, duty_paid (optional)</p>
                <div className="flex items-center gap-3">
                  <label className="flex-1">
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-blue-300 transition-colors cursor-pointer">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 font-medium">Click to upload CSV</p>
                      <p className="text-xs text-slate-400 mt-1">CSV file with import entry data</p>
                    </div>
                    <input type="file" accept=".csv" className="hidden" onChange={handleCSVUpload} disabled={scanning} data-testid="ieepa-csv-upload" />
                  </label>
                </div>
                {scanning && (
                  <div className="flex items-center gap-2 mt-3 text-blue-600">
                    <Loader2 className="w-4 h-4 animate-spin" /> Scanning entries...
                  </div>
                )}
              </div>

              {/* Manual Entry */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-outfit font-semibold text-[#0B3D91] flex items-center gap-2">
                    <FileText className="w-5 h-5" /> Manual Entry
                  </h3>
                  <Button size="sm" variant="outline" onClick={addManualEntry} data-testid="ieepa-add-entry-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Entry
                  </Button>
                </div>

                <div className="space-y-3">
                  {manualEntries.map((entry, idx) => (
                    <div key={idx} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 p-3 bg-slate-50 rounded-lg" data-testid={`manual-entry-${idx}`}>
                      <input placeholder="Entry #" value={entry.entry_number} onChange={e => updateEntry(idx, 'entry_number', e.target.value)} className="px-3 py-2 text-sm border rounded-lg" />
                      <input type="date" value={entry.entry_date} onChange={e => updateEntry(idx, 'entry_date', e.target.value)} className="px-3 py-2 text-sm border rounded-lg" data-testid={`entry-date-${idx}`} />
                      <select value={entry.country_of_origin} onChange={e => updateEntry(idx, 'country_of_origin', e.target.value)} className="px-3 py-2 text-sm border rounded-lg" data-testid={`entry-country-${idx}`}>
                        <option value="">Country</option>
                        {countries.map(c => <option key={c.country_code} value={c.country_code}>{c.country_name} ({c.country_code})</option>)}
                      </select>
                      <input placeholder="HS Code" value={entry.hs_code} onChange={e => updateEntry(idx, 'hs_code', e.target.value)} className="px-3 py-2 text-sm border rounded-lg" />
                      <input placeholder="Customs Value" type="number" value={entry.customs_value} onChange={e => updateEntry(idx, 'customs_value', e.target.value)} className="px-3 py-2 text-sm border rounded-lg" data-testid={`entry-value-${idx}`} />
                      <input placeholder="Duty Paid (opt)" type="number" value={entry.duty_paid} onChange={e => updateEntry(idx, 'duty_paid', e.target.value)} className="px-3 py-2 text-sm border rounded-lg" />
                      <button onClick={() => removeManualEntry(idx)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors self-center justify-self-center">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <Button onClick={handleManualScan} disabled={scanning} className="mt-4 bg-[#0B3D91] hover:bg-[#082A66] rounded-full" data-testid="ieepa-manual-scan-btn">
                  {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</> : <><Search className="w-4 h-4 mr-2" /> Scan Entries</>}
                </Button>
              </div>
            </div>
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <div className="space-y-6" data-testid="ieepa-results-tab">
              {selectedScan ? (
                <>
                  {/* Summary */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-outfit font-semibold text-[#0B3D91]">{selectedScan.scan_name}</h3>
                        <p className="text-xs text-slate-400">{new Date(selectedScan.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={handleExportPDF} data-testid="ieepa-export-pdf-btn">
                          <Download className="w-4 h-4 mr-1" /> PDF Report
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleExportCAPE(selectedScan.scan_id)} data-testid="ieepa-export-cape-btn">
                          <Download className="w-4 h-4 mr-1" /> Export CAPE CSV
                        </Button>
                        <Button size="sm" onClick={() => handleCreateClaim(selectedScan.scan_id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ieepa-create-claim-btn">
                          <FileText className="w-4 h-4 mr-1" /> Create Claim
                        </Button>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-4 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500">Total Entries</p>
                        <p className="text-xl font-bold text-slate-900">{selectedScan.total_entries}</p>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500">Eligible</p>
                        <p className="text-xl font-bold text-emerald-700">{selectedScan.total_eligible_entries}</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500">Potential Refund</p>
                        <p className="text-xl font-bold text-blue-700">${selectedScan.total_potential_refund?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-violet-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500">Total Recovery</p>
                        <p className="text-xl font-bold text-violet-700">${selectedScan.total_recovery?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Entry Details */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <h3 className="font-outfit font-semibold text-[#0B3D91] mb-4">Entry Details</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-3 text-slate-500 font-medium">Entry #</th>
                            <th className="text-left py-3 px-3 text-slate-500 font-medium">Date</th>
                            <th className="text-left py-3 px-3 text-slate-500 font-medium">Country</th>
                            <th className="text-left py-3 px-3 text-slate-500 font-medium">HS Code</th>
                            <th className="text-right py-3 px-3 text-slate-500 font-medium">Value</th>
                            <th className="text-right py-3 px-3 text-slate-500 font-medium">IEEPA Rate</th>
                            <th className="text-right py-3 px-3 text-slate-500 font-medium">Duty</th>
                            <th className="text-right py-3 px-3 text-slate-500 font-medium">Interest</th>
                            <th className="text-right py-3 px-3 text-slate-500 font-medium">Recovery</th>
                            <th className="text-center py-3 px-3 text-slate-500 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedScan.results?.map((r, i) => (
                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`result-row-${i}`}>
                              <td className="py-3 px-3 font-mono text-xs">{r.entry_number || '-'}</td>
                              <td className="py-3 px-3">{r.entry_date}</td>
                              <td className="py-3 px-3">{r.country_name || r.country}</td>
                              <td className="py-3 px-3 font-mono">{r.hs_code || '-'}</td>
                              <td className="py-3 px-3 text-right">${r.customs_value?.toLocaleString()}</td>
                              <td className="py-3 px-3 text-right">{r.ieepa_rate || '-'}</td>
                              <td className="py-3 px-3 text-right font-medium">${r.duty_paid?.toLocaleString() || '-'}</td>
                              <td className="py-3 px-3 text-right text-blue-600">${r.interest_accrued?.toLocaleString() || '-'}</td>
                              <td className="py-3 px-3 text-right font-bold text-emerald-600">${r.total_recovery?.toLocaleString() || '-'}</td>
                              <td className="py-3 px-3 text-center">
                                {r.eligible ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
                                    <CheckCircle className="w-3 h-3" /> Eligible
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-xs font-medium rounded-full">
                                    <XCircle className="w-3 h-3" /> {r.reason || 'Ineligible'}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
                  <Eye className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No scan selected. Run a new scan or select from history.</p>
                  <Button variant="outline" className="mt-4" onClick={() => setActiveTab('scan')}>Start a Scan</Button>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4" data-testid="ieepa-history-tab">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="font-outfit font-semibold text-[#0B3D91] mb-4">Scan History</h3>
                {scans.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-8">No scans yet. Start your first scan to see history here.</p>
                ) : (
                  <div className="space-y-2">
                    {scans.map(scan => (
                      <div key={scan.scan_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => handleViewScan(scan.scan_id)} data-testid={`scan-history-${scan.scan_id}`}>
                        <div>
                          <p className="font-medium text-slate-900">{scan.scan_name}</p>
                          <p className="text-xs text-slate-500">{scan.scan_type} · {scan.total_entries} entries · {scan.total_eligible_entries} eligible</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">${scan.total_recovery?.toLocaleString()}</p>
                          <p className="text-xs text-slate-400">{new Date(scan.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Claims Tab */}
          {activeTab === 'claims' && (
            <div className="space-y-4" data-testid="ieepa-claims-tab">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="font-outfit font-semibold text-[#0B3D91] mb-4">Claims</h3>
                {claims.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-8">No claims yet. Create a claim from scan results.</p>
                ) : (
                  <div className="space-y-2">
                    {claims.map(claim => (
                      <div key={claim.claim_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => openClaimDetail(claim.claim_id)} data-testid={`claim-${claim.claim_id}`}>
                        <div>
                          <p className="font-medium text-slate-900">{claim.claim_id}</p>
                          <p className="text-xs text-slate-500">{claim.total_entries} entries · Scan: {claim.scan_id}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-bold text-blue-700">${claim.total_claim_amount?.toLocaleString()}</p>
                            <p className="text-xs text-slate-400">{new Date(claim.created_at).toLocaleDateString()}</p>
                          </div>
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                            claim.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                            claim.status === 'submitted' ? 'bg-blue-50 text-blue-700' :
                            claim.status === 'refunded' ? 'bg-purple-50 text-purple-700' :
                            claim.status === 'processing' ? 'bg-amber-50 text-amber-700' :
                            claim.status === 'reviewed' ? 'bg-teal-50 text-teal-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {claim.status}
                          </span>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Claim Detail View */}
          {activeTab === 'claim-detail' && selectedClaim && (
            <div className="space-y-5" data-testid="ieepa-claim-detail">
              {/* Back + Header */}
              <div className="flex items-center gap-3">
                <button onClick={() => { setSelectedClaim(null); setActiveTab('claims'); }} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="claim-back-btn">
                  <ChevronUp className="w-5 h-5 text-slate-600 -rotate-90" />
                </button>
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-[#0B3D91] text-lg">{selectedClaim.claim_id}</h3>
                  <p className="text-xs text-slate-500">Created {new Date(selectedClaim.created_at).toLocaleDateString()} · {selectedClaim.total_entries} entries</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-700">${selectedClaim.total_claim_amount?.toLocaleString()}</p>
                </div>
              </div>

              {/* Status + Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-600">Status:</span>
                    <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                      selectedClaim.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                      selectedClaim.status === 'submitted' ? 'bg-blue-50 text-blue-700' :
                      selectedClaim.status === 'refunded' ? 'bg-purple-50 text-purple-700' :
                      selectedClaim.status === 'processing' ? 'bg-amber-50 text-amber-700' :
                      selectedClaim.status === 'reviewed' ? 'bg-teal-50 text-teal-700' :
                      'bg-slate-100 text-slate-600'
                    }`} data-testid="claim-status-badge">
                      {selectedClaim.status}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {selectedClaim.status === 'draft' && (
                      <Button size="sm" onClick={() => updateClaimStatus('submitted')} className="bg-blue-600 hover:bg-blue-700 rounded-full" data-testid="claim-submit-btn">Submit Claim</Button>
                    )}
                    {selectedClaim.status === 'submitted' && (
                      <Button size="sm" onClick={() => updateClaimStatus('processing')} className="bg-amber-600 hover:bg-amber-700 rounded-full" data-testid="claim-process-btn">Mark Processing</Button>
                    )}
                    {selectedClaim.status === 'processing' && (
                      <Button size="sm" onClick={() => updateClaimStatus('reviewed')} className="bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="claim-review-btn">Mark Reviewed</Button>
                    )}
                    {selectedClaim.status === 'reviewed' && (
                      <Button size="sm" onClick={() => updateClaimStatus('approved')} className="bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid="claim-approve-btn">Approve</Button>
                    )}
                    {selectedClaim.status === 'approved' && (
                      <Button size="sm" onClick={() => updateClaimStatus('refunded')} className="bg-purple-600 hover:bg-purple-700 rounded-full" data-testid="claim-refund-btn">Mark Refunded</Button>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  {['draft','submitted','processing','reviewed','approved','refunded'].map((s, i) => (
                    <div key={s} className={`flex-1 h-1.5 rounded-full ${
                      ['draft','submitted','processing','reviewed','approved','refunded'].indexOf(selectedClaim.status) >= i
                        ? 'bg-blue-500' : 'bg-slate-100'
                    }`} />
                  ))}
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-5">
                {/* Document Checklist */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="claim-checklist">
                  <h4 className="font-outfit font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0B3D91]" /> Document Checklist
                    <span className="ml-auto text-xs font-normal text-slate-400">
                      {(selectedClaim.checklist || []).filter(c => c.checked).length}/{(selectedClaim.checklist || []).length}
                    </span>
                  </h4>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 mb-4">
                    <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{
                      width: `${(selectedClaim.checklist || []).length > 0 ? ((selectedClaim.checklist || []).filter(c => c.checked).length / (selectedClaim.checklist || []).length * 100) : 0}%`
                    }} />
                  </div>
                  <div className="space-y-2">
                    {(selectedClaim.checklist || []).map(item => (
                      <label key={item.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`checklist-${item.id}`}>
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={(e) => toggleChecklist(item.id, e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className={`text-sm ${item.checked ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                          {item.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="claim-timeline">
                  <h4 className="font-outfit font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#0B3D91]" /> Activity Timeline
                  </h4>
                  <div className="space-y-3 max-h-[320px] overflow-y-auto">
                    {(selectedClaim.timeline || []).slice().reverse().map((entry, i) => (
                      <div key={i} className="flex gap-3 text-sm" data-testid={`timeline-${i}`}>
                        <div className="flex flex-col items-center">
                          <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${
                            entry.event === 'claim_created' ? 'bg-blue-500' :
                            entry.event === 'status_change' ? 'bg-emerald-500' :
                            entry.event === 'note_added' ? 'bg-amber-500' :
                            'bg-slate-300'
                          }`} />
                          {i < (selectedClaim.timeline || []).length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                        </div>
                        <div className="pb-3">
                          <p className="font-medium text-slate-800">
                            {entry.event === 'claim_created' ? 'Claim created' :
                             entry.event === 'status_change' ? `Status: ${entry.old_status} → ${entry.status}` :
                             entry.event === 'note_added' ? 'Note added' :
                             entry.event === 'checklist_toggle' ? `Checklist: ${entry.checked ? 'checked' : 'unchecked'}` :
                             entry.event}
                          </p>
                          <p className="text-xs text-slate-400">{entry.user_name} · {new Date(entry.timestamp).toLocaleString()}</p>
                          {entry.note_preview && <p className="text-xs text-slate-500 mt-0.5 italic">&quot;{entry.note_preview}&quot;</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="claim-notes">
                <h4 className="font-outfit font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#0B3D91]" /> Notes & Comments
                  <span className="text-xs font-normal text-slate-400 ml-auto">{(selectedClaim.notes || []).length} notes</span>
                </h4>

                {/* Add note */}
                <div className="flex gap-2 mb-4">
                  <input
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addNote()}
                    placeholder="Add a note..."
                    className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    data-testid="claim-note-input"
                  />
                  <Button size="sm" onClick={addNote} disabled={!newNote.trim()} className="bg-[#0B3D91] hover:bg-[#082A66] rounded-lg px-4" data-testid="claim-add-note-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Notes list */}
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {(selectedClaim.notes || []).length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>
                  ) : (
                    (selectedClaim.notes || []).slice().reverse().map(note => (
                      <div key={note.note_id} className="p-3 bg-slate-50 rounded-lg group" data-testid={`note-${note.note_id}`}>
                        <div className="flex items-start justify-between">
                          <p className="text-sm text-slate-700 flex-1">{note.text}</p>
                          <button onClick={() => deleteNote(note.note_id)} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-2" data-testid={`delete-note-${note.note_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{note.author} · {new Date(note.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Countries Reference Tab */}
          {activeTab === 'countries' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="ieepa-countries-tab">
              <h3 className="font-outfit font-semibold text-[#0B3D91] mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5" /> IEEPA-Affected Countries & Rates
              </h3>
              <p className="text-sm text-slate-500 mb-4">Reciprocal tariff rates imposed under IEEPA executive orders (Apr 2025 - Feb 2026)</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Country</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Code</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Original Rate</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Adjusted Rate</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Adjusted Date</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">HTS Suffix</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countries.map(c => (
                      <tr key={c.country_code} className="border-b border-slate-50 hover:bg-blue-50/50" data-testid={`country-row-${c.country_code}`}>
                        <td className="py-2.5 px-4 font-medium">{c.country_name}</td>
                        <td className="py-2.5 px-4 font-mono text-xs">{c.country_code}</td>
                        <td className="py-2.5 px-4 text-right font-semibold text-red-600">{c.original_rate}</td>
                        <td className="py-2.5 px-4 text-right font-semibold text-orange-600">{c.adjusted_rate || '-'}</td>
                        <td className="py-2.5 px-4 text-slate-500">{c.adjusted_date || '-'}</td>
                        <td className="py-2.5 px-4 font-mono text-xs">{c.hts_suffix}</td>
                        <td className="py-2.5 px-4 text-xs text-slate-400">{c.note || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default IEEPARecovery;
